<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { Link, router } from '@inertiajs/vue3'
import { Avatar, AvatarFallback, AvatarImage } from '@/Components/ui/avatar'
import axios from 'axios'
import CancelOccurrenceModal from './CancelOccurrenceModal.vue'
import { useScrollLock } from '@/composables/useScrollLock'
import { minutesUntilLesson } from '@/lib/lessonTime'
import {
  Calendar,
  Clock,
  Users,
  User,
  BookOpen,
  StickyNote,
  Save,
  X,
  Loader2,
  CheckCircle,
  AlertCircle,
  Mail,
  GraduationCap,
  CalendarX2,
  RefreshCw,
  Video,
  ArrowLeft,
  ChevronRight,
} from 'lucide-vue-next'

interface LessonLinks {
  room_id: string
  teacher_link: string
  student_link: string
  generated_at: string
}

interface Session {
  id: number
  title: string
  date: string
  time: string
  raw_time?: string
  type: 'one-on-one' | 'group'
  status: 'completed' | 'ongoing' | 'upcoming' | 'fixed'
  subject_name?: string
  level_name?: string
  total_enrollments?: number
  max_participants?: number
  note?: string | null
  topic?: string | null
  duration_minutes?: number
  day_of_week?: string
  schedule_id?: number
  series_id?: string | null
  lesson_links?: LessonLinks | null
}

interface Student {
  id: number
  name: string
  email: string
  avatar_url: string | null
  status: string
  attended: boolean
  tier: string | null
}

const props = withDefaults(defineProps<{
  show: boolean
  session: Session | null
  canEditNote?: boolean
}>(), {
  canEditNote: true
})

const emit = defineEmits<{
  close: []
  noteUpdated: [session: Session]
  sessionUpdated: []
  sessionCancelled: []
}>()

useScrollLock(() => props.show)

// View mode: 'view' or 'edit'
const viewMode = ref<'view' | 'edit'>('view')

const isEditingNote = ref(false)
const noteText = ref('')
const isSaving = ref(false)
const isLoadingStudents = ref(false)
const students = ref<Student[]>([])

// Cancel modal state
const showCancelModal = ref(false)

// Lessonspace states
const generatingLesson = ref(false)
const lessonLinks = ref<LessonLinks | null>(null)
const roomError = ref<string | null>(null)

// A room can only be created for a materialised session (positive schedule id)
// that has at least one enrolled student — mirrors the server-side guard so the
// button never fires a request that is bound to fail.
const roomBlockedReason = computed<string | null>(() => {
  const s = props.session
  if (!s) return null
  const scheduleId = s.schedule_id ?? s.id
  if (scheduleId < 0) return 'This session is not available yet — it opens closer to its start time.'
  if ((s.total_enrollments ?? 0) < 1) return 'No students are enrolled in this session yet.'
  return null
})

// Edit form state
const editForm = ref({
  display_name: '',
  date: '',
  time: '',
  note: '',
})
const editProcessing = ref(false)
const editError = ref<string | null>(null)
const showRescheduleWarning = ref(false)

// Reset state when session changes
watch(() => props.session, async (newSession) => {
  if (newSession) {
    noteText.value = newSession.note || ''
    isEditingNote.value = false
    viewMode.value = 'view'
    students.value = []
    lessonLinks.value = newSession.lesson_links || null
    editError.value = null

    // Fetch enrolled students if canEditNote (teacher view)
    if (props.canEditNote) {
      await fetchEnrolledStudents()
    }
  }
}, { immediate: true })

// Fetch enrolled students
async function fetchEnrolledStudents() {
  if (!props.session) return

  const scheduleId = props.session.schedule_id || props.session.id
  if (!scheduleId) return

  isLoadingStudents.value = true
  try {
    const response = await axios.get(`/teacher/schedules/${scheduleId}/enrolled-students`, {
      params: { occurrence_date: props.session.date }
    })

    if (response.data?.success && Array.isArray(response.data.students)) {
      students.value = response.data.students
    }
  } catch (error) {
    console.error('Failed to fetch enrolled students:', error)
  } finally {
    isLoadingStudents.value = false
  }
}

// Format date for display
function formatDate(dateString: string) {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

// Get status config
function getStatusConfig(status: string) {
  switch (status) {
    case 'completed':
      return { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Completed' }
    case 'ongoing':
      return { color: 'bg-emerald-100 text-emerald-800', icon: Video, label: 'In Progress' }
    case 'upcoming':
      return { color: 'bg-amber-100 text-amber-800', icon: Clock, label: 'Upcoming' }
    default:
      return { color: 'bg-blue-100 text-blue-800', icon: Calendar, label: 'Scheduled' }
  }
}

// Whether the student should see the lesson room link (ongoing or upcoming with links)
const showStudentLessonLink = computed(() => {
  if (props.canEditNote) return false // Teacher uses the full lesson section
  if (!props.session) return false
  if (!lessonLinks.value) return false
  return ['ongoing', 'upcoming'].includes(props.session.status)
})

// Start editing note
function startEditNote() {
  isEditingNote.value = true
}

// Cancel editing note
function cancelEditNote() {
  if (props.session) {
    noteText.value = props.session.note || ''
  }
  isEditingNote.value = false
}

// Save note
async function saveNote() {
  if (!props.session) return

  isSaving.value = true

  try {
    const scheduleId = props.session.schedule_id || props.session.id
    const response = await axios.put(`/teacher/schedules/${scheduleId}/note`, {
      note: noteText.value,
      occurrence_date: props.session.date
    })

    if (response.data.success) {
      const updatedSession = { ...props.session, note: noteText.value }
      emit('noteUpdated', updatedSession)
      isEditingNote.value = false
    }
  } catch (error: any) {
    console.error('Failed to save note:', error)
    alert(error.response?.data?.message || 'Failed to save note')
  } finally {
    isSaving.value = false
  }
}

// Check if note has changed
const hasNoteChanged = computed(() => {
  return noteText.value !== (props.session?.note || '')
})

const statusConfig = computed(() => props.session ? getStatusConfig(props.session.status) : null)

// Minutes remaining until session starts, read as a UK time (null if no valid date/time)
const minutesToSession = computed(() =>
  minutesUntilLesson(props.session?.date, props.session?.raw_time),
)

// Check if session can be cancelled or rescheduled (not completed and more than 1 hour away)
const canModifySession = computed(() => {
  if (props.session?.status === 'completed') return false
  if (minutesToSession.value !== null && minutesToSession.value < 60) return false
  return true
})

// Human-readable time until session
const timeToSessionLabel = computed(() => {
  const mins = minutesToSession.value
  if (mins === null) return null
  if (mins <= 0) return 'Session has started'
  if (mins < 60) return `Starts in ${mins} min`
  const hours = Math.floor(mins / 60)
  const remainingMins = mins % 60
  if (remainingMins === 0) return `Starts in ${hours}h`
  return `Starts in ${hours}h ${remainingMins}m`
})

// Lesson room URL — prefix depends on role (canEditNote = teacher view)
const lessonRoomUrl = computed(() => {
  if (!props.session) return null
  const prefix = props.canEditNote ? '/teacher' : '/student'
  return `${prefix}/lessons/${props.session.schedule_id || props.session.id}/${props.session.date}`
})

// Map session to schedule prop format for cancel modal
const scheduleForCancel = computed(() => {
  if (!props.session) return null
  return {
    id: props.session.schedule_id || props.session.id,
    name: props.session.title,
    series_id: props.session.series_id || '',
  }
})

const occurrenceForCancel = computed(() => {
  if (!props.session) return null
  return {
    schedule_id: props.session.schedule_id || props.session.id,
    date: props.session.date,
    time: props.session.raw_time || props.session.time,
    current_enrollments: props.session.total_enrollments || 0,
    max_participants: props.session.max_participants || 1,
    display_name: props.session.title,
    topic: props.session.topic || undefined,
  }
})

// Handle cancel session - open cancel modal
function handleCancel() {
  if (!props.session) return
  showCancelModal.value = true
}

// Switch to edit form view
function handleReschedule() {
  if (!props.session) return
  editForm.value = {
    display_name: props.session.title,
    date: props.session.date,
    time: props.session.raw_time || props.session.time,
    note: props.session.note || '',
  }
  editError.value = null
  viewMode.value = 'edit'
}

// Go back to view mode
function backToView() {
  viewMode.value = 'view'
  editError.value = null
  showRescheduleWarning.value = false
}

// Show reschedule warning before submitting
function submitEdit() {
  showRescheduleWarning.value = true
}

// Confirmed submit after warning
function confirmReschedule() {
  if (!props.session) return

  editProcessing.value = true
  editError.value = null
  showRescheduleWarning.value = false
  const scheduleId = props.session.schedule_id || props.session.id

  router.post(
    `/teacher/schedules/${scheduleId}/occurrences/${props.session.date}/edit`,
    editForm.value,
    {
      preserveScroll: true,
      onSuccess: () => {
        viewMode.value = 'view'
        emit('close')
        emit('sessionUpdated')
      },
      onError: (errors: any) => {
        editError.value = errors.message || 'Failed to update session'
      },
      onFinish: () => {
        editProcessing.value = false
      }
    }
  )
}

// Handle cancel modal cancelled
function onOccurrenceCancelled() {
  showCancelModal.value = false
  emit('close')
  emit('sessionCancelled')
}

// Lessonspace: generate lesson space
async function generateLessonSpace() {
  if (!props.session) return
  generatingLesson.value = true
  roomError.value = null

  try {
    const scheduleId = props.session.schedule_id || props.session.id
    const response = await axios.post(`/teacher/schedules/${scheduleId}/generate-lesson-space`)

    if (response.data.success) {
      lessonLinks.value = response.data.links
    }
  } catch (error: any) {
    console.error('Failed to generate lesson space:', error)
    roomError.value = error.response?.data?.message || 'Failed to generate lesson space'
  } finally {
    generatingLesson.value = false
  }
}
</script>

<template>
  <!--
    Teleported to the body: `position: fixed` is measured against the nearest
    ancestor carrying a transform, filter or containment, not against the
    viewport, and the calendar grid this modal opens from has them. Left in
    place, the dialog was positioned against that ancestor instead and drifted
    with the page scroll, most often with its header off the top of the screen.
  -->
  <Teleport to="body">
    <!-- Modal Overlay -->
    <Transition name="modal">
    <div
      v-if="show && session"
      class="fixed inset-0 z-50 overflow-y-auto"
      @click.self="emit('close')"
    >
      <!-- Backdrop -->
      <div class="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"></div>

      <!-- Modal Container -->
      <div class="flex min-h-full items-center justify-center p-4">
        <div
          class="relative flex flex-col bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90dvh] overflow-hidden transform transition-all"
          @click.stop
        >
          <!-- Header with Gradient -->
          <div :class="[
            'relative shrink-0 px-6 py-5',
            session.type === 'group'
              ? 'bg-linear-to-r from-teal-500 to-teal-800'
              : 'bg-linear-to-r from-teal-500 to-teal-800'
          ]">
            <!-- Close / Back Button -->
            <button
              v-if="viewMode === 'edit'"
              type="button"
              @click="backToView"
              class="absolute top-4 right-4 text-white/80 hover:text-white transition-colors hover:bg-white/20 rounded-full p-1.5"
            >
              <ArrowLeft class="w-5 h-5" />
            </button>
            <button
              v-else
              type="button"
              @click="emit('close')"
              class="absolute top-4 right-4 text-white/80 hover:text-white transition-colors hover:bg-white/20 rounded-full p-1.5"
            >
              <X class="w-5 h-5" />
            </button>

            <!-- Session Type Badge -->
            <div class="flex items-center gap-2 mb-3">
              <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-white/20 text-white backdrop-blur-sm">
                <component :is="session.type === 'group' ? Users : User" class="w-3.5 h-3.5" />
                {{ session.type === 'group' ? 'Group Session' : '1:1 Session' }}
              </span>
              <span
                v-if="statusConfig && viewMode === 'view'"
                :class="['inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold', statusConfig.color]"
              >
                <component :is="statusConfig.icon" class="w-3 h-3" />
                {{ statusConfig.label }}
              </span>
              <span
                v-if="viewMode === 'edit'"
                class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-white/20 text-white"
              >
                <RefreshCw class="w-3 h-3" />
                Editing
              </span>
            </div>

            <!-- Title -->
            <h2 class="text-xl font-bold text-white pr-8">
              {{ viewMode === 'edit' ? 'Edit Session' : session.title }}
            </h2>

            <!-- Subject & Level -->
            <p v-if="session.subject_name" class="text-white/80 text-sm mt-1 flex items-center gap-1.5">
              <BookOpen class="w-4 h-4" />
              {{ session.level_name ? `${session.level_name} ` : '' }}{{ session.subject_name }}
            </p>
          </div>

          <!-- ==================== VIEW MODE ==================== -->
          <template v-if="viewMode === 'view'">
            <!-- Scrollable Body -->
            <div class="flex-1 overflow-y-auto min-h-0">
              <div class="p-6 space-y-5">
                <!-- Date & Time Cards -->
                <div class="grid gap-3 sm:grid-cols-2">
                  <div class="flex items-start gap-3 p-4 rounded-xl bg-gray-50 border border-gray-100">
                    <div class="p-2 rounded-lg bg-blue-100">
                      <Calendar class="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p class="text-xs font-medium text-gray-500 uppercase tracking-wide">Date</p>
                      <p class="text-sm font-semibold text-gray-900 mt-0.5">{{ formatDate(session.date) }}</p>
                    </div>
                  </div>

                  <div class="flex items-start gap-3 p-4 rounded-xl bg-gray-50 border border-gray-100">
                    <div class="p-2 rounded-lg bg-purple-100">
                      <Clock class="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p class="text-xs font-medium text-gray-500 uppercase tracking-wide">Time</p>
                      <p class="text-sm font-semibold text-gray-900 mt-0.5">
                        {{ session.time }}
                        <span v-if="session.duration_minutes" class="text-gray-500 font-normal">
                          ({{ session.duration_minutes }} min)
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Lesson Space Section (teacher view only) -->
                <div v-if="canEditNote" class="flex items-center gap-3 p-4 rounded-xl border" :class="lessonLinks ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-100'">
                  <div class="p-2 rounded-lg" :class="lessonLinks ? 'bg-emerald-100' : 'bg-gray-200'">
                    <Video class="w-5 h-5" :class="lessonLinks ? 'text-emerald-600' : 'text-gray-500'" />
                  </div>
                  <div class="flex-1">
                    <p class="text-xs font-medium text-gray-500 uppercase tracking-wide">Lesson Room</p>
                    <p v-if="lessonLinks" class="text-sm font-semibold text-emerald-700 mt-0.5">Room ready</p>
                    <p v-else class="text-sm text-gray-500 mt-0.5">No room created yet</p>
                  </div>
                  <div class="flex items-center gap-2">
                    <!-- Go to Room link -->
                    <Link
                      v-if="lessonLinks"
                      :href="lessonRoomUrl!"
                      class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Video class="w-3.5 h-3.5" />
                      Go to Room
                    </Link>
                    <!-- Generate button -->
                    <button
                      v-if="!lessonLinks"
                      @click="generateLessonSpace"
                      :disabled="generatingLesson || roomBlockedReason !== null"
                      :title="roomBlockedReason ?? ''"
                      class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-600 bg-purple-100 rounded-lg hover:bg-purple-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Loader2 v-if="generatingLesson" class="w-3.5 h-3.5 animate-spin" />
                      <Video v-else class="w-3.5 h-3.5" />
                      {{ generatingLesson ? 'Creating...' : 'Create Room' }}
                    </button>
                  </div>
                </div>
                <p v-if="roomBlockedReason && !lessonLinks" class="px-1 -mt-1 text-xs text-gray-500">{{ roomBlockedReason }}</p>
                <p v-if="roomError" class="px-1 -mt-1 text-xs font-medium text-red-600">{{ roomError }}</p>

                <!-- Lesson Room Link (student view - ongoing/upcoming sessions with links) -->
                <div v-if="showStudentLessonLink" class="flex items-center gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                  <div class="p-2 rounded-lg bg-emerald-100">
                    <Video class="w-5 h-5 text-emerald-600" />
                  </div>
                  <div class="flex-1">
                    <p class="text-xs font-medium text-gray-500 uppercase tracking-wide">Lesson Room</p>
                    <p class="text-sm font-semibold text-emerald-700 mt-0.5">
                      {{ session.status === 'ongoing' ? 'Session in progress' : 'Room ready' }}
                    </p>
                  </div>
                  <Link
                    :href="lessonRoomUrl!"
                    class="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
                  >
                    <Video class="w-4 h-4" />
                    Join Session
                  </Link>
                </div>

                <!-- Topic -->
                <div v-if="session.topic" class="p-4 rounded-xl bg-amber-50 border border-amber-100">
                  <div class="flex items-center gap-2 mb-2">
                    <GraduationCap class="w-4 h-4 text-amber-600" />
                    <p class="text-xs font-medium text-amber-700 uppercase tracking-wide">Topic</p>
                  </div>
                  <p class="text-sm text-gray-900">{{ session.topic }}</p>
                </div>

                <!-- Enrolled Students (Teacher View) -->
                <div v-if="canEditNote" class="space-y-3">
                  <div class="flex items-center justify-between">
                    <h3 class="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <Users class="w-4 h-4 text-gray-500" />
                      Enrolled Students
                    </h3>
                    <span class="text-xs text-gray-500">{{ students.length }} student{{ students.length !== 1 ? 's' : '' }}</span>
                  </div>

                  <!-- Loading State -->
                  <div v-if="isLoadingStudents" class="flex items-center justify-center py-8">
                    <Loader2 class="w-6 h-6 text-gray-400 animate-spin" />
                  </div>

                  <!-- Empty State -->
                  <div v-else-if="students.length === 0" class="text-center py-6 px-4 rounded-xl bg-gray-50 border border-dashed border-gray-200">
                    <Users class="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p class="text-sm text-gray-500">No students enrolled yet</p>
                  </div>

                  <!-- Student List -->
                  <div v-else class="space-y-2">
                    <Link
                      v-for="student in students"
                      :key="student.id"
                      :href="`/teacher/students/${student.id}`"
                      class="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors border border-gray-100"
                    >
                      <Avatar class="w-10 h-10 border-2 border-white shadow-sm">
                        <AvatarImage
                          :src="student.avatar_url"
                          :alt="student.name"
                        />
                        <AvatarFallback class="bg-gray-100 text-gray-400">
                          <User class="w-5 h-5" />
                        </AvatarFallback>
                      </Avatar>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-semibold text-gray-900 truncate">{{ student.name }}</p>
                        <p class="text-xs text-gray-500 truncate flex items-center gap-1">
                          <Mail class="w-3 h-3" />
                          {{ student.email }}
                        </p>
                      </div>
                      <div class="flex items-center gap-2">
                        <span
                          v-if="student.tier"
                          :class="[
                            'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                            student.tier === 'Gold' ? 'bg-amber-100 text-amber-700' :
                            student.tier === 'Silver' ? 'bg-gray-100 text-gray-700' :
                            'bg-blue-100 text-blue-700'
                          ]"
                        >
                          {{ student.tier }}
                        </span>
                        <ChevronRight class="w-4 h-4 text-gray-400" />
                      </div>
                    </Link>
                  </div>
                </div>

                <!-- Note Section -->
                <div class="space-y-3">
                  <div class="flex items-center justify-between">
                    <h3 class="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <StickyNote class="w-4 h-4 text-gray-500" />
                      Session Notes
                    </h3>
                    <button
                      v-if="canEditNote && !isEditingNote"
                      @click="startEditNote"
                      class="text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
                    >
                      {{ session.note ? 'Edit Note' : 'Add Note' }}
                    </button>
                  </div>

                  <!-- Note Display Mode -->
                  <div v-if="!isEditingNote" class="p-4 rounded-xl bg-amber-50 border border-amber-100 min-h-20">
                    <p v-if="session.note" class="text-sm text-gray-900 whitespace-pre-wrap">
                      {{ session.note }}
                    </p>
                    <p v-else class="text-sm text-gray-400 italic">
                      No notes for this session yet.
                    </p>
                  </div>

                  <!-- Note Edit Mode -->
                  <div v-else-if="canEditNote" class="space-y-3">
                    <textarea
                      v-model="noteText"
                      rows="4"
                      class="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none bg-white"
                      placeholder="Add notes about this session..."
                    ></textarea>
                    <div class="flex items-center justify-end gap-2">
                      <button
                        @click="cancelEditNote"
                        :disabled="isSaving"
                        class="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        @click="saveNote"
                        :disabled="isSaving || !hasNoteChanged"
                        class="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                      >
                        <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
                        <Save v-else class="w-4 h-4" />
                        {{ isSaving ? 'Saving...' : 'Save Note' }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- View Mode Footer -->
            <div class="shrink-0 px-6 py-4 border-t border-gray-100 bg-gray-50/80">
              <!-- Time-to-session warning when less than 1 hour -->
              <div v-if="canEditNote && !canModifySession && timeToSessionLabel && session?.status !== 'completed'" class="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200">
                <Clock class="w-4 h-4 text-amber-600 shrink-0" />
                <p class="text-xs text-amber-800">{{ timeToSessionLabel }} — reschedule and cancellation are disabled within 1 hour of the session.</p>
              </div>

              <div class="flex items-center gap-3">
                <!-- Cancel button hidden for now
                <button
                  v-if="canEditNote && canModifySession"
                  @click="handleCancel"
                  class="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-colors"
                >
                  <CalendarX2 class="w-4 h-4" />
                  Cancel Session
                </button>
                -->

                <button
                  v-if="canEditNote && session?.status !== 'completed'"
                  :disabled="!canModifySession"
                  @click="handleReschedule"
                  :class="[
                    'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-colors',
                    canModifySession
                      ? 'text-blue-600 bg-blue-50 border border-blue-200 hover:bg-blue-100'
                      : 'text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed'
                  ]"
                >
                  <RefreshCw class="w-4 h-4" />
                  Reschedule
                </button>

                <button
                  @click="emit('close')"
                  class="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors shadow-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </template>

          <!-- ==================== EDIT MODE ==================== -->
          <template v-if="viewMode === 'edit'">
            <div class="flex-1 overflow-y-auto min-h-0">
              <!-- Info Banner -->
              <div class="bg-blue-50 px-6 py-3">
                <div class="flex gap-2 text-sm text-blue-800">
                  <AlertCircle class="mt-0.5 w-5 h-5 shrink-0 text-blue-600" />
                  <p>Changes will only apply to this specific session. Other sessions in the series will remain unchanged.</p>
                </div>
              </div>

              <!-- Error Message -->
              <div v-if="editError" class="mx-6 mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-800">
                {{ editError }}
              </div>

              <!-- Edit Form -->
              <form @submit.prevent="submitEdit" class="p-6 space-y-5">
                <!-- Session Title -->
                <div>
                  <label for="edit-title" class="block text-sm font-medium text-gray-700">Session Title</label>
                  <input
                    id="edit-title"
                    v-model="editForm.display_name"
                    type="text"
                    required
                    class="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="e.g., Advanced Algebra Session"
                  />
                  <p class="mt-1.5 text-xs text-gray-500">Custom title for this specific session</p>
                </div>

                <!-- Date and Time -->
                <div class="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label for="edit-date" class="block text-sm font-medium text-gray-700">Date</label>
                    <input
                      id="edit-date"
                      v-model="editForm.date"
                      type="date"
                      required
                      class="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label for="edit-time" class="block text-sm font-medium text-gray-700">Start Time</label>
                    <input
                      id="edit-time"
                      v-model="editForm.time"
                      type="time"
                      required
                      class="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <!-- Read-only Session Info -->
                <div class="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                  <h4 class="text-sm font-semibold text-gray-700">Session Information</h4>
                  <div class="grid gap-3 text-sm sm:grid-cols-2">
                    <div>
                      <span class="text-gray-500">Duration:</span>
                      <span class="ml-2 font-medium text-gray-900">{{ session.duration_minutes || 60 }} minutes</span>
                    </div>
                    <div>
                      <span class="text-gray-500">Capacity:</span>
                      <span class="ml-2 font-medium text-gray-900">{{ session.total_enrollments || 0 }}/{{ session.max_participants || 1 }} students</span>
                    </div>
                    <div v-if="session.topic" class="sm:col-span-2">
                      <span class="text-gray-500">Topic:</span>
                      <span class="ml-2 font-medium text-gray-900">{{ session.topic }}</span>
                    </div>
                  </div>
                </div>

                <!-- Session Notes -->
                <div>
                  <label for="edit-note" class="block text-sm font-medium text-gray-700">Session Notes</label>
                  <textarea
                    id="edit-note"
                    v-model="editForm.note"
                    rows="4"
                    class="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Add any special instructions or information for this session..."
                  />
                  <p class="mt-1.5 text-xs text-gray-500">These notes will be visible to enrolled students</p>
                </div>
              </form>
            </div>

            <!-- Reschedule Warning -->
            <div v-if="showRescheduleWarning" class="shrink-0 border-t border-amber-200 bg-amber-50 px-6 py-4">
              <div class="flex gap-3">
                <AlertCircle class="mt-0.5 w-5 h-5 shrink-0 text-amber-600" />
                <div class="flex-1">
                  <p class="text-sm font-semibold text-amber-900">Confirm reschedule</p>
                  <p class="mt-1 text-sm text-amber-800">
                    <template v-if="(session?.total_enrollments || 0) > 0">
                      {{ session?.total_enrollments }} enrolled student{{ (session?.total_enrollments || 0) !== 1 ? 's' : '' }} will be notified about this change via email.
                    </template>
                    <template v-else>
                      Are you sure you want to reschedule this session?
                    </template>
                  </p>
                  <div class="mt-3 flex items-center gap-2">
                    <button
                      @click="confirmReschedule"
                      :disabled="editProcessing"
                      class="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
                    >
                      <Loader2 v-if="editProcessing" class="w-3.5 h-3.5 animate-spin" />
                      {{ editProcessing ? 'Saving...' : 'Yes, Reschedule' }}
                    </button>
                    <button
                      @click="showRescheduleWarning = false"
                      :disabled="editProcessing"
                      class="px-4 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100 rounded-lg transition-colors disabled:opacity-50"
                    >
                      Go Back
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Edit Mode Footer -->
            <div v-if="!showRescheduleWarning" class="shrink-0 px-6 py-4 border-t border-gray-100 bg-gray-50/80">
              <div class="flex items-center justify-end gap-3">
                <button
                  @click="backToView"
                  :disabled="editProcessing"
                  class="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors shadow-sm disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  @click="submitEdit"
                  :disabled="editProcessing"
                  class="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Loader2 v-if="editProcessing" class="w-4 h-4 animate-spin" />
                  <Save v-else class="w-4 h-4" />
                  {{ editProcessing ? 'Saving...' : 'Save Changes' }}
                </button>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
    </Transition>
  </Teleport>

  <!-- Cancel Occurrence Modal (separate since it's a destructive confirmation) -->
  <CancelOccurrenceModal
    v-if="showCancelModal && occurrenceForCancel && scheduleForCancel"
    :occurrence="occurrenceForCancel"
    :schedule="scheduleForCancel"
    @close="showCancelModal = false"
    @cancelled="onOccurrenceCancelled"
  />
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active > div:last-child > div:last-child,
.modal-leave-active > div:last-child > div:last-child {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.modal-enter-from > div:last-child > div:last-child,
.modal-leave-to > div:last-child > div:last-child {
  transform: scale(0.95) translateY(10px);
  opacity: 0;
}
</style>
